import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, unref as _unref, createBlock as _createBlock, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = ["data-testid"]
const _hoisted_2 = ["data-testid"]
const _hoisted_3 = { class: "text-sm text-textPrimary" }
const _hoisted_4 = {
  key: 2,
  class: "flex flex-col gap-4"
}
const _hoisted_5 = ["data-testid"]
const _hoisted_6 = ["data-testid"]
const _hoisted_7 = ["data-testid"]
const _hoisted_8 = {
  key: 0,
  class: "text-textPrimary ml-2"
}
const _hoisted_9 = {
  key: 1,
  class: "text-textPrimary ml-2"
}

import { computed, defineComponent, h, onBeforeUnmount, ref, watch, type Ref } from 'vue'
import Network from '@/Network'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import { type LabelBadgeColor } from '@/components/designSystem/Badges/LabelBadge.vue'
import GoogleMaps from '@/components/GoogleMaps.vue'
import ProtectedImage from '@/modules/company/Approvals/FacialBiometry/components/ProtectedImage.vue'
import type {
  CheckVerificationResponse,
  VerificationEvent,
  VerificationSide
} from '@/Network/Types/Responses/CheckVerificationResponses'
import {
  VERIFICATION_COVERAGE_LABELS,
  VERIFICATION_DISTANCE_ESTIMATED_LABEL,
  VERIFICATION_MISSING_REASON_LABELS,
  VERIFICATION_MODAL_TITLES,
  VERIFICATION_PHOTO_RESTRICTED_TEXT,
  VERIFICATION_PHOTO_RESTRICTED_TITLE
} from '@/constants/check-verification.constants'
import { getApiErrorMessage } from '@/utils/apiError'

/** O que o pai precisa para montar cabeçalho e rodapé em volta deste painel. */
export interface CheckVerificationContext {
  title: string
  coverageLabel: string
  coverageColor: LabelBadgeColor
  mapsUrl: string | null
  isBiometry: boolean
}

const CHECKIN: VerificationEvent = 'checkin'
const CHECKOUT: VerificationEvent = 'checkout'

const MUTED_CLASS = 'text-gray-500'
const WARN_TEXT_CLASS = 'text-[#B8641B]'
const CHIP_CLASS = 'inline-flex px-2 py-0.5 rounded-full text-xs font-bold uppercase'
const PLACEHOLDER_CLASS = 'rounded-md border border-dashed border-gray-300 p-4 text-center text-sm'

interface SideCardProps {
  event: VerificationEvent
  side: VerificationSide | null
  isBiometry: boolean
  blobUrl: string | null
  missingLabel: string
}

type Vnode = ReturnType<typeof h>

/** Placeholder do lado ausente: sem registro, sem foto ou check-out por permanência. */

export default /*#__PURE__*/_defineComponent({
  __name: 'CheckVerificationPanel',
  props: {
    checkId: {},
    events: { default: () => ['checkin', 'checkout'] },
    testidPrefix: { default: 'verification' },
    mapHeightPx: { default: 220 }
  },
  emits: ["loaded", "failed"],
  setup(__props: any, { expose: __expose, emit }) {

const props = __props;





/** Ordem fixa dos dois lados existentes. `events` é o recorte renderizado. */
const ALL_EVENTS = ['checkin', 'checkout'] as const
const loading = ref(false)
const error = ref<string | null>(null)
const data = ref<CheckVerificationResponse | null>(null)
const blobUrls = ref<Record<VerificationEvent, string | null>>({ checkin: null, checkout: null })

const eventLabel = (k: VerificationEvent) => (k === CHECKIN ? 'Check-in' : 'Check-out')

const isBiometry = computed(() => data.value?.checkMethod === 'BIOMETRIC')
const title = computed(() =>
  data.value ? VERIFICATION_MODAL_TITLES[data.value.title] : 'Verificação do registro'
)
const coverageLabel = computed(() =>
  data.value ? VERIFICATION_COVERAGE_LABELS[data.value.coverage] : ''
)
// LabelBadge nao tem `purple`: cobertura completa usa `green` (biometria) / `blue`
// (somente geolocalizacao); cobertura parcial usa `orange`.
const coverageColor = computed<LabelBadgeColor>(() => {
  if (data.value?.coverage !== 'BOTH') return 'orange'
  return isBiometry.value ? 'green' : 'blue'
})
const sideOk = (k: VerificationEvent) =>
  Boolean(data.value?.sides[k]) &&
  (!isBiometry.value || Boolean(data.value?.sides[k]?.photo.available))
// Só os lados RENDERIZADOS pintam a barra de âmbar: numa aba de check-in, um
// check-out faltando não é pendência daquela aba.
const statusBarWarn = computed(() => props.events.some((k) => !sideOk(k)))
const missingLabel = (k: VerificationEvent): string => {
  const s = data.value?.sides[k]
  if (!s)
    return k === CHECKOUT
      ? VERIFICATION_MISSING_REASON_LABELS.NOT_REGISTERED
      : 'Check-in não registrado'
  return s.missingReason ? VERIFICATION_MISSING_REASON_LABELS[s.missingReason] : ''
}
const mapPoints = computed(() =>
  props.events.flatMap((k) => {
    const s = data.value?.sides[k]
    return s && s.latitude != null && s.longitude != null
      ? [
          {
            id: k,
            lat: s.latitude,
            lng: s.longitude,
            label: eventLabel(k),
            color: k === CHECKIN ? '#3A7D4F' : '#6B4FA3'
          }
        ]
      : []
  })
)
const hasMap = computed(() => Boolean(data.value?.unit.latitude != null || mapPoints.value.length))
const mapCenter = computed(() => ({
  lat: data.value?.unit.latitude ?? mapPoints.value[0]?.lat ?? 0,
  lng: data.value?.unit.longitude ?? mapPoints.value[0]?.lng ?? 0
}))
const mapsUrl = computed(() => {
  // O primeiro lado RENDERIZADO que tenha coordenada; com os dois lados isto é o
  // `checkin ?? checkout` de sempre.
  const s = props.events.map((k) => data.value?.sides[k]).find(Boolean)
  const u = data.value?.unit
  return s && u && s.latitude != null && u.latitude != null
    ? `https://www.google.com/maps/dir/${s.latitude},${s.longitude}/${u.latitude},${u.longitude}`
    : null
})
const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '')

function revokeAll() {
  // Percorre os DOIS lados, não só os renderizados: `events` pode ter mudado
  // depois de uma foto já ter sido baixada.
  for (const k of ALL_EVENTS) {
    if (blobUrls.value[k]) URL.revokeObjectURL(blobUrls.value[k] as string)
    blobUrls.value[k] = null
  }
}

// Token da requisicao em voo: incrementado a cada load() e a cada desmontagem.
// Guarda contra a corrida em que o painel some (revokeAll ja rodou) e um
// getPhotoBlob() antigo resolve depois, criando um blob URL orfao que so seria
// liberado no proximo load() ou no unmount.
const loadToken = ref(0)

async function load() {
  if (!props.checkId) return
  const id = props.checkId
  const token = ++loadToken.value
  loading.value = true
  error.value = null
  revokeAll()
  data.value = null
  try {
    const res = await Network.doctorCheckVerification.getVerification(id)
    if (token !== loadToken.value) return
    data.value = res
    emit('loaded', {
      title: title.value,
      coverageLabel: coverageLabel.value,
      coverageColor: coverageColor.value,
      mapsUrl: mapsUrl.value,
      isBiometry: isBiometry.value
    })
    await Promise.all(
      props.events.map(async (k) => {
        const s = res.sides[k]
        if (!s?.photo.available || !s.photo.allowed) return
        try {
          const blob = await Network.doctorCheckVerification.getPhotoBlob(id, k)
          if (token === loadToken.value) blobUrls.value[k] = URL.createObjectURL(blob)
        } catch (e) {
          console.error(e)
        }
      })
    )
  } catch (e) {
    if (token === loadToken.value) {
      error.value = getApiErrorMessage(e, 'Não foi possível carregar a verificação do registro')
      emit('failed')
    }
  } finally {
    if (token === loadToken.value) loading.value = false
  }
}

/**
 * O gatilho é o `checkId`, não um `isOpen`: o painel só existe enquanto está
 * montado, e quem decide isso é o pai (o `Modal` destrói o slot ao fechar; o modal
 * de evidências só monta a aba visitada). Assim nenhum consumidor busca escondido.
 */
watch(
  () => props.checkId,
  () => void load(),
  { immediate: true }
)

onBeforeUnmount(() => {
  loadToken.value++
  revokeAll()
})

__expose({ load })

const renderPlaceholder = (event: VerificationEvent, label: string): Vnode =>
  h('div', { class: PLACEHOLDER_CLASS, 'data-testid': `${props.testidPrefix}-missing-${event}` }, [
    h('b', { class: WARN_TEXT_CLASS }, label)
  ])

/** Foto existe no registro, mas o perfil do usuário não pode vê-la. */
const renderRestricted = (): Vnode =>
  h(
    'div',
    {
      class: 'rounded-md bg-[#F0F0F4] p-4 text-center text-sm',
      'data-testid': `${props.testidPrefix}-photo-restricted`
    },
    [
      h('div', { class: 'text-xl' }, '🔒'),
      h('b', VERIFICATION_PHOTO_RESTRICTED_TITLE),
      h('p', { class: 'text-xs text-gray-500' }, VERIFICATION_PHOTO_RESTRICTED_TEXT)
    ]
  )

/** Foto de biometria protegida, com botão de rotação de 90°. */
const renderPhoto = (event: VerificationEvent, blobUrl: string, rotation: Ref<number>): Vnode =>
  h(
    'div',
    {
      class:
        'relative bg-[#F0F0F4] rounded-md overflow-hidden flex items-center justify-center max-h-[220px]'
    },
    [
      h(ProtectedImage, {
        blobUrl,
        rotation: rotation.value,
        imgClass: 'w-full h-full object-contain p-2',
        'data-testid': `${props.testidPrefix}-photo-${event}`
      }),
      h(
        'button',
        {
          class: 'absolute top-2 right-2 w-8 h-8 rounded-md bg-white shadow',
          'aria-label': 'Girar foto',
          onClick: () => {
            rotation.value = (rotation.value + 90) % 360
          }
        },
        '↻'
      )
    ]
  )

/** Área de mídia do card: foto, aviso de restrição ou placeholder. `null` quando só há geolocalização. */
function renderMedia(p: SideCardProps, rotation: Ref<number>): Vnode | null {
  const s = p.side
  if (!p.isBiometry) return s ? null : renderPlaceholder(p.event, p.missingLabel)
  if (!s?.photo.available)
    return renderPlaceholder(p.event, p.missingLabel || VERIFICATION_MISSING_REASON_LABELS.NO_PHOTO)
  if (!s.photo.allowed) return renderRestricted()
  if (!p.blobUrl) return renderPlaceholder(p.event, VERIFICATION_MISSING_REASON_LABELS.NO_PHOTO)
  return renderPhoto(p.event, p.blobUrl, rotation)
}

/** Sufixo da distância: rótulo de estimativa ou comparação com o raio de alcance. */
function distanceSuffix(s: VerificationSide): string {
  if (s.distanceSource === 'ESTIMATED') return ` (${VERIFICATION_DISTANCE_ESTIMATED_LABEL})`
  if (s.reachRadiusM == null) return ''
  const within = s.withinRadius ? 'Dentro do raio' : 'Fora do raio'
  return ` · ${within} (${Math.round(s.reachRadiusM)} m)`
}

/** Linhas de detalhe do lado: comparação facial, distância e user agent. */
function renderRows(s: VerificationSide | null): Vnode[] {
  const rows: Vnode[] = []
  if (s?.face)
    rows.push(
      h('div', [
        h('span', { class: MUTED_CLASS }, 'Comparação '),
        h('span', s.face.match ? 'Compatível' : 'Não compatível'),
        s.face.distance != null
          ? h('span', ` · ${s.face.distance.toFixed(2)} / ${(s.face.threshold ?? 0).toFixed(2)}`)
          : null
      ])
    )
  if (s?.distanceM != null)
    rows.push(
      h('div', [
        h('span', { class: MUTED_CLASS }, 'Distância '),
        h(
          'b',
          { class: s.withinRadius === false ? 'text-[#A93F3F]' : 'text-[#3A7D4F]' },
          `${Math.round(s.distanceM)} m`
        ),
        h('span', distanceSuffix(s))
      ])
    )
  if (s?.userAgent)
    rows.push(h('div', [h('span', { class: MUTED_CLASS }, 'App '), h('span', s.userAgent)]))
  return rows
}

/** Um lado da verificação: foto (ou placeholder/restrita), hora, comparação, distância. */
const VerificationSideCard = defineComponent({
  props: {
    event: { type: String as () => VerificationEvent, required: true },
    side: { type: Object as () => VerificationSide | null, default: null },
    isBiometry: Boolean,
    blobUrl: { type: String, default: null },
    missingLabel: { type: String, default: '' }
  },
  setup(p) {
    const rotation = ref(0)
    const label = eventLabel(p.event)
    const chipColor = (s: VerificationSide | null) => {
      if (!s) return 'bg-[#F0F0F4] text-gray-500'
      return p.isBiometry ? 'bg-[#E8E1F5] text-[#6B4FA3]' : 'bg-[#DBE8F3] text-[#2F6F9F]'
    }
    const time = () =>
      p.side
        ? new Date(p.side.at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
        : '—'
    return () => {
      const s = p.side
      const head = h('div', { class: 'flex items-center gap-2 font-semibold text-sm' }, [
        h('span', { class: [CHIP_CLASS, chipColor(s)] }, label),
        h('span', { class: 'ml-auto text-textPrimary font-normal tabular-nums' }, time())
      ])
      return h(
        'div',
        {
          class: 'flex flex-col gap-2 min-w-0',
          'data-testid': `${props.testidPrefix}-side-${p.event}`
        },
        [
          head,
          renderMedia(p, rotation),
          h('div', { class: 'text-xs flex flex-col gap-0.5' }, renderRows(s))
        ]
      )
    }
  }
})

return (_ctx: any,_cache: any) => {
  return (loading.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: "min-h-[300px] rounded-lg bg-lightGray30 animate-pulse",
        "data-testid": `${_ctx.testidPrefix}-skeleton`
      }, null, 8, _hoisted_1))
    : (error.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: "min-h-[200px] flex flex-col items-center justify-center gap-3 text-center",
          "data-testid": `${_ctx.testidPrefix}-error`
        }, [
          _createElementVNode("p", _hoisted_3, _toDisplayString(error.value), 1),
          _createVNode(OutlineButton, {
            text: "Tentar novamente",
            width: "200px",
            "data-testid": `${_ctx.testidPrefix}-retry-button`,
            onClick: load
          }, null, 8, ["data-testid"])
        ], 8, _hoisted_2))
      : (data.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("div", {
              class: _normalizeClass(["grid grid-cols-1 gap-4", _ctx.events.length > 1 ? 'md:grid-cols-2' : ''])
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.events, (key) => {
                return (_openBlock(), _createBlock(_unref(VerificationSideCard), {
                  key: key,
                  event: key,
                  side: data.value.sides[key],
                  "is-biometry": isBiometry.value,
                  "blob-url": blobUrls.value[key],
                  "missing-label": missingLabel(key)
                }, null, 8, ["event", "side", "is-biometry", "blob-url", "missing-label"]))
              }), 128))
            ], 2),
            (hasMap.value)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: "rounded-lg overflow-hidden border border-gray-30",
                  style: _normalizeStyle({ height: `${_ctx.mapHeightPx}px` }),
                  "data-testid": `${_ctx.testidPrefix}-map`
                }, [
                  _createVNode(GoogleMaps, {
                    latitude: mapCenter.value.lat,
                    longitude: mapCenter.value.lng,
                    radius: data.value.unit.reachRadiusM ?? undefined,
                    points: mapPoints.value,
                    draggable: false,
                    "show-info": false,
                    "fit-to-points": true
                  }, null, 8, ["latitude", "longitude", "radius", "points"])
                ], 12, _hoisted_5))
              : _createCommentVNode("", true),
            _createElementVNode("div", {
              class: _normalizeClass(["flex flex-wrap items-center gap-2 rounded-md px-3 py-2 text-sm", statusBarWarn.value ? 'bg-[#F8E8D8] is-warn' : 'bg-[#F0F0F4]']),
              "data-testid": `${_ctx.testidPrefix}-status-bar`
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.events, (key) => {
                return (_openBlock(), _createElementBlock("span", {
                  key: key,
                  "data-testid": `${_ctx.testidPrefix}-status-${key}`,
                  class: _normalizeClass(["inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold uppercase", sideOk(key) ? 'bg-[#DCEFE2] text-[#3A7D4F]' : 'bg-[#F8E8D8] text-[#B8641B]'])
                }, _toDisplayString(sideOk(key) ? '✓' : '✗') + " " + _toDisplayString(eventLabel(key)) + _toDisplayString(sideOk(key) ? '' : ' faltando'), 11, _hoisted_7))
              }), 128)),
              (data.value.referencePhoto)
                ? (_openBlock(), _createElementBlock("span", _hoisted_8, [
                    _createTextVNode("Foto de referência aprovada em " + _toDisplayString(formatDate(data.value.referencePhoto.approvedAt)) + " ", 1),
                    (data.value.referencePhoto.approvedByName)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createTextVNode(" por " + _toDisplayString(data.value.referencePhoto.approvedByName), 1)
                        ], 64))
                      : _createCommentVNode("", true)
                  ]))
                : (!isBiometry.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_9, "Método: geolocalização · sem biometria nesta escala"))
                  : _createCommentVNode("", true)
            ], 10, _hoisted_6)
          ]))
        : _createCommentVNode("", true)
}
}

})